import {
  CircularProgress,
  Divider,
  IconButton,
  Typography,
} from "@mui/material";
import { classes } from "common/theme/theme";
import { DndContext, DragEndEvent } from "@dnd-kit/core";
import { Job, Objective, ObjectiveAssignment } from "protos/rtc/jobs";
import { jobExplorer } from "common/state/jobExplorer";
import { Link } from "react-router-dom";
import { Path } from "portal/utils/routing";
import { reprioritize } from "../../reprioritize";
import { RobotData } from "protos/rtc/location_history";
import { rtcJobsApi } from "common/state/rtcJobsApi";
import { SortableContainer } from "portal/components/Sortable";
import { t } from "i18next";
import { useMutationPopups } from "portal/utils/hooks/useApiPopups";
import { useSelector } from "react-redux";
import { useStandardDragDropSensors } from "portal/utils/hooks/useStandardDragDropSensors";
import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import JobIcon from "@mui/icons-material/AssignmentOutlined";
import React, { FC, useMemo, useState } from "react";

type DecoratedObjectiveAssignment = ObjectiveAssignment & {
  job: Job | undefined;
  objective: Objective | undefined;
};

interface TractorObjectiveQueueProps {
  tractor: (RobotData & { serial: string }) | undefined;
  jobsById: { [jobId: number]: Job };
  objectivesById: { [objectiveId: number]: Objective };
  activeObjectiveId: number | undefined;
  objectiveAssignments: ObjectiveAssignment[];
}

export const TractorObjectiveQueue: FC<TractorObjectiveQueueProps> = ({
  tractor,
  jobsById,
  objectivesById,
  activeObjectiveId,
  objectiveAssignments,
}) => {
  const assignmentsById = useMemo(
    () =>
      Object.fromEntries(objectiveAssignments.map((asgn) => [asgn.id, asgn])),
    [objectiveAssignments]
  );
  const job = useSelector(jobExplorer.selectors.selectJob);
  const sensors = useStandardDragDropSensors();
  const [readOnly, setReadOnly] = useState<boolean>(false);
  const [updateAssignmentPriorities] = useMutationPopups(
    rtcJobsApi.endpoints.updateAssignmentPriorities.useMutation()
  );

  interface SortableObjectiveAssignment {
    id: string;
    index: number;
    assignment: DecoratedObjectiveAssignment;
    priority: number;
  }

  const { canonicalQueue, hasActiveHead } = useMemo<{
    canonicalQueue: SortableObjectiveAssignment[];
    hasActiveHead: boolean;
  }>(() => {
    const decoratedAssignments = objectiveAssignments.map((asgn, index) => {
      const objective = objectivesById[asgn.objectiveId] ?? undefined;
      const job = objective?.jobId ? jobsById[objective.jobId] : undefined;

      return {
        id: asgn.id.toString(),
        index,
        assignment: {
          ...asgn,
          objective,
          job,
        },
        priority: asgn.priority ?? 0,
      };
    });

    // "canonicalize" the assignment queue by putting the active objective's
    // assignment (if any) at the head, so that we can compute the right indexes
    // to reprioritize and make sure no inactive objectives get prioritized
    // before the active one
    const activeAssignment = decoratedAssignments.find(
      (asgn) => asgn.assignment.objectiveId === activeObjectiveId
    );

    if (activeAssignment) {
      return {
        canonicalQueue: [
          activeAssignment,
          ...decoratedAssignments.filter(
            (asgn) => asgn.assignment.objectiveId !== activeObjectiveId
          ),
        ],
        hasActiveHead: true,
      };
    } else {
      return { canonicalQueue: decoratedAssignments, hasActiveHead: false };
    }
  }, [activeObjectiveId, jobsById, objectiveAssignments, objectivesById]);

  // the active assignment is displayed separately above the queue, so hide it
  // here; when there is no active assignment the whole queue is sortable
  const sortableAssignments = hasActiveHead
    ? canonicalQueue.slice(1)
    : canonicalQueue;

  const [removeAssignment] = useMutationPopups(
    rtcJobsApi.endpoints.removeAssignment.useMutation()
  );

  const render: (o: SortableObjectiveAssignment) => JSX.Element = ({
    assignment,
  }) => {
    return (
      <div key={assignment.id} className="flex flex-col ">
        <div className="flex justify-between">
          <div className="flex items-center">
            <DragIndicatorIcon
              className={classes("ml-2 mr-2", { "opacity-0": readOnly })}
            />
            <Typography className="flex my-auto">
              {assignment.objective?.name}
            </Typography>
            {tractor?.active &&
              tractor.objectiveId === assignment.objectiveId && (
                <span className="flex my-auto">
                  <CircularProgress color="info" className="h-5 w-5 " />
                </span>
              )}
          </div>
          <div className="flex justify-between gap-4">
            {assignment.job?.id !== job?.id && (
              <Link
                target="_blank"
                to={`${Path.JOBS}/${assignment.job?.id}/orchestrate`}
                className="flex my-auto gap-1"
              >
                <JobIcon color="warning" className="flex my-auto" />
                <Typography>{assignment.job?.name}</Typography>
              </Link>
            )}
            <IconButton
              color="info"
              className="flex my-auto"
              onClick={() =>
                removeAssignment({ objectiveId: assignment.objectiveId })
              }
            >
              <CloseIcon />
            </IconButton>
          </div>
        </div>
        <Divider />
      </div>
    );
  };

  async function handleDragEnd(event: DragEndEvent): Promise<void> {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndexInSortableQueue = sortableAssignments.findIndex(
        ({ id }) => id === active.id
      );
      const newIndexInSortableQueue = sortableAssignments.findIndex(
        ({ id }) => id === over.id
      );

      if (oldIndexInSortableQueue === -1) {
        console.error(
          `TractorObjectiveQueue: couldn't find active id ${active.id}`
        );
        return;
      }

      if (newIndexInSortableQueue === -1) {
        console.error(
          `TractorObjectiveQueue: couldn't find over id ${over.id}`
        );
        return;
      }

      // when the active assignment is hidden at the head of the canonical queue,
      // the sortable part is the 1st...nth elements; add 1 to both indexes
      const sortableOffset = hasActiveHead ? 1 : 0;
      const oldIndexInCanonicalQueue = oldIndexInSortableQueue + sortableOffset;
      const newIndexInCanonicalQueue = newIndexInSortableQueue + sortableOffset;

      const { modifiedPriorities } = reprioritize(
        canonicalQueue,
        oldIndexInCanonicalQueue,
        newIndexInCanonicalQueue
      );

      // turn off drag-drop while we do the potentially non-atomic update to
      // the objective priority list on the server
      setReadOnly(true);
      await updateAssignmentPriorities({
        assignments: [...modifiedPriorities.entries()].flatMap(
          ([id, priority]) =>
            assignmentsById[id]
              ? {
                  ...assignmentsById[id],
                  priority,
                }
              : []
        ),
      });
      setReadOnly(false);
    }
  }

  return (
    <div className="flex flex-col overflow-x-hidden">
      {sortableAssignments.length === 0 ? (
        <div>
          <Typography className="flex my-auto px-4 text-slate-400">
            {t("utils.descriptors.none")}
          </Typography>
        </div>
      ) : (
        <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
          <SortableContainer
            id="objective-assignments"
            items={sortableAssignments}
            render={render}
            disabled={readOnly}
          />
        </DndContext>
      )}
    </div>
  );
};
